/**
 * Global error capture for every Aven frontend (Vue via `setupVueErrorHandler`,
 * React via direct call): `window.onerror` + `unhandledrejection` + the
 * stale-chunk auto-reload guard. Idempotent per page.
 */

export interface LogSink {
    info: (template: string, args?: Record<string, unknown>) => void
    warn: (template: string, args?: Record<string, unknown>) => void
    error: (template: string, args?: Record<string, unknown>) => void
}

// Network-noise messages are demoted to warn so they don't page on flaky transport.
export const DEMOTED_ERROR_MESSAGES: RegExp[] = [
    /Network Error/gi, // A temporary network problem that is /usually/ on the client's side / mobile networks / etc
    /Unexpected token/gi, // This is caused when we deploy while someone is on the site and they get a 404 from the missing file
    /SyntaxError/gi, // This pops up on offbrand browsers, esp ones that inject their own JS
    /Loading.*chunk.*failed./gi, // This is also caused by a bad client-side cache and/or deploy
    /Request aborted/gi, // This is caused when an in-flight HTTP request is cancelled (while reloading, changing pages, etc)
    /Script error\./gi, // This is caused when a script loaded from a third-party fails for any reason. Browser does not provide any details even about the third-party origin (see: https://sentry.io/answers/script-error/)
]

// Chunk-reload attempts tracked in a URL param (not sessionStorage) so they survive the
// reload and work in iframe / private-browsing contexts where sessionStorage may throw.
const CHUNK_RELOAD_PARAM = '_chunkReload'
const MAX_CHUNK_RELOAD_ATTEMPTS = 2

let installed = false

function readChunkReloadCount(): number {
    try {
        const raw = new URLSearchParams(window.location.search).get(CHUNK_RELOAD_PARAM)
        const n = raw ? parseInt(raw, 10) : 0
        return Number.isFinite(n) && n >= 0 ? n : 0
    } catch {
        return 0
    }
}

function redirectWithIncrementedChunkReloadCount(currentCount: number): void {
    try {
        const url = new URL(window.location.href)
        url.searchParams.set(CHUNK_RELOAD_PARAM, String(currentCount + 1))
        window.location.replace(url.toString())
    } catch {
        window.location.reload()
    }
}

/**
 * Wire `window.onerror` + `unhandledrejection` into `log`. Chunk-load failures
 * trigger a rate-limited auto-reload; network-noise messages are demoted to warn.
 * Idempotent; call once at boot (or let `createLogger().setupVueErrorHandler`
 * do it for you on Vue apps).
 */
export function installGlobalErrorHandlers(log: LogSink): void {
    if (typeof window === 'undefined' || installed) {
        return
    }
    installed = true

    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const reason = event.reason
        const reasonMessage = String(reason?.message ?? '')
        if (/Loading.*chunk.*failed./i.test(reasonMessage)) {
            const reloadCount = readChunkReloadCount()
            if (reloadCount < MAX_CHUNK_RELOAD_ATTEMPTS) {
                log.info('Reloading page to fix stale chunk error', { priorAttempts: reloadCount })
                redirectWithIncrementedChunkReloadCount(reloadCount)
                return
            }
            log.error('Chunk load failure persisting after auto-reload attempts; surfacing instead of looping', {
                priorAttempts: reloadCount,
                error: reason,
            })
            return
        }
        if (DEMOTED_ERROR_MESSAGES.some((re) => re.test(String(reason)))) {
            log.warn('window.onunhandledrejection error', { error: reason })
        } else {
            log.error('window.onunhandledrejection error', { error: reason })
        }
    })

    window.addEventListener('error', (event: ErrorEvent) => {
        const { message, filename, lineno, colno, error } = event
        if (DEMOTED_ERROR_MESSAGES.some((re) => re.test(String(message)))) {
            log.warn('window.onerror error', { message, filename, lineno, colno, error })
        } else {
            log.error('window.onerror error', { message, filename, lineno, colno, error })
        }
    })
}
